import { useQuery } from "@tanstack/react-query";
import { dashboardApi } from "../../lib/api";
import type { DashboardReportMeta } from "../../types/api";
import { DashboardWidgetShell } from "./DashboardWidgetShell";
import { BarChartWidget, LineChartWidget } from "./widgets/ChartWidgets";
import { InfoCardWidget } from "./widgets/InfoCardWidget";
import { TableWidget } from "./widgets/TableWidget";

interface WidgetRendererProps {
  report: DashboardReportMeta;
  editMode?: boolean;
  onRemove?: () => void;
}

export function WidgetRenderer({ report, editMode, onRemove }: WidgetRendererProps) {
  const runQuery = useQuery({
    queryKey: ["dashboard-report", report.key],
    queryFn: () => dashboardApi.runReport(report.key),
    staleTime: 60_000,
  });

  const run = runQuery.data;
  const errorMessage = runQuery.isError
    ? runQuery.error instanceof Error
      ? runQuery.error.message
      : "Failed to load report"
    : null;

  return (
    <DashboardWidgetShell
      report={report}
      interpretation={run?.interpretation}
      loading={runQuery.isLoading}
      error={errorMessage}
      editMode={editMode}
      onRemove={onRemove}
    >
      {run && (
        <>
          {run.element_type === "infocard" && <InfoCardWidget report={report} run={run} />}
          {run.element_type === "barchart" && <BarChartWidget run={run} />}
          {run.element_type === "linechart" && <LineChartWidget run={run} />}
          {run.element_type === "table" && <TableWidget report={report} run={run} />}
        </>
      )}
    </DashboardWidgetShell>
  );
}
